import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Phone, MessageCircle, Award, Scale, Building2, ShieldCheck } from "lucide-react";
import SEO from "../components/SEO";
import ServiceCard from "../components/ServiceCard";
import { services } from "../data/services";
import { clientSectors } from "../data/firm";

const HERO_IMG =
  "https://images.unsplash.com/photo-1729346878607-0dfc1295ee5a?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400";
const ABOUT_IMG =
  "https://images.unsplash.com/photo-1621589074311-e801705e2bdb?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const stats = [
  { num: "2006", label: "Year Established" },
  { num: "35+", label: "Years Combined Bar" },
  { num: "500+", label: "Reported Decisions" },
  { num: "25+", label: "Juniors Mentored" },
];

const homeFaqs = [
  { q: "Where is K & K Associates located in Delhi?", a: "Our office is at A-197, Lower Ground Floor, Defence Colony, New Delhi 110024 — a short distance from Saket District Court, Patiala House and the Delhi High Court at Sher Shah Road. We serve clients across Delhi NCR (Gurgaon, Noida, Faridabad, Ghaziabad) and pan-India through a vetted network." },
  { q: "What types of matters does the firm handle?", a: "Thirty distinct practice areas including litigation, intellectual property, arbitration, corporate advisory, mergers & acquisitions, real estate, matrimonial, succession, SARFAESI, debt recovery, banking & finance, white collar crimes, Companies Act & NCLT, motor accident claims, consumer protection, electricity, industrial disputes, cheque bouncing, landlord-tenant, employee compensation, child custody, insurance, PIL & writs, legal opinions, startup advisory, legal notices and policy drafting." },
  { q: "How do I book a first consultation?", a: "You can call +91 96435 72870, WhatsApp +91 96435 72870, email info@kandkassociates.in or fill the consultation form on our Contact page. A partner reviews every enquiry and responds within one business day." },
  { q: "What does an initial consultation cost?", a: "First consultations are scoped during the call. We provide a written engagement letter with milestone-linked or capped retainer fees before any work begins. Predictability is part of our service standard." },
  { q: "Do you act for individuals or only corporations?", a: "Both. We act for individuals (matrimonial, succession, motor accident, consumer, property), MSMEs, founders, listed companies, banks, NBFCs and overseas clients. The level of service and partner attention is the same, regardless of mandate size." },
  { q: "Can you handle matters outside Delhi?", a: "Yes. We routinely conduct matters across India — Mumbai, Bengaluru, Hyderabad, Kolkata, Chennai and tier-2 cities — through a vetted local-counsel network with central oversight from Defence Colony." },
  { q: "What languages do your lawyers work in?", a: "English, Hindi and Punjabi. Court proceedings are conducted in the prescribed language of the forum; we handle translations and certified document conversion in-house." },
];

const homeSchema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "LegalService",
      "@id": "https://kandkassociates.com/#legalservice",
      name: "K & K Associates - Advocates & Solicitors",
      url: "https://kandkassociates.com/",
      telephone: "+91-96435-72870",
      email: "info@kandkassociates.in",
      priceRange: "₹₹",
      foundingDate: "2006",
      address: {
        "@type": "PostalAddress",
        streetAddress: "A-197, LGF, Defence Colony",
        addressLocality: "New Delhi",
        postalCode: "110024",
        addressRegion: "Delhi",
        addressCountry: "IN",
      },
      geo: { "@type": "GeoCoordinates", latitude: 28.5722, longitude: 77.2305 },
      areaServed: ["India", "Delhi NCR", "New Delhi", "Gurgaon", "Noida", "Faridabad", "Ghaziabad"],
    },
    {
      "@type": "WebSite",
      url: "https://kandkassociates.com/",
      name: "K & K Associates",
      potentialAction: {
        "@type": "SearchAction",
        target: "https://kandkassociates.com/services?q={search_term_string}",
        "query-input": "required name=search_term_string",
      },
    },
    {
      "@type": "FAQPage",
      mainEntity: homeFaqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ],
};

export default function Home() {
  return (
    <>
      <SEO
        title="Advocates & Solicitors in New Delhi"
        description="K & K Associates — Advocates & Solicitors in Defence Colony, New Delhi. World-class services across Litigation, IP, Arbitration, Corporate Advisory, M&A, Real Estate and 30 practice areas, serving Delhi NCR and pan-India since 2006."
        keywords={[
          "K and K Associates",
          "law firm Defence Colony",
          "advocates Delhi",
          "solicitors New Delhi",
          "litigation lawyers Delhi",
          "corporate law firm India",
        ]}
        canonical="https://kandkassociates.com/"
        schema={homeSchema}
      />

      {/* HERO */}
      <section className="relative min-h-[92vh] md:min-h-screen flex flex-col justify-end overflow-hidden bg-ink text-white">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Modern legal architecture" className="w-full h-full object-cover opacity-50" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/30" />
        </div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12 pt-28 md:pt-40 pb-10 md:pb-14 w-full">
          <div className="grid grid-cols-12 gap-8 items-end">
            <div className="col-span-12 lg:col-span-9">
              <p className="kicker text-white/80 mb-6" data-testid="hero-kicker">
                Estd. — Advocates &amp; Solicitors · New Delhi
              </p>
              <h1
                className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl leading-[0.98] sm:leading-[0.95] tracking-tight"
                style={{ fontWeight: 400 }}
                data-testid="hero-headline"
              >
                The discipline of trial.<br />
                <span className="italic font-normal text-white/70" style={{ fontWeight: 300 }}>The wisdom of</span> not&nbsp;litigating.
              </h1>
              <p className="mt-6 md:mt-10 max-w-2xl font-serif text-lg md:text-2xl text-white/85 leading-relaxed">
                K &amp; K Associates is a New Delhi based law firm built around a single commitment —
                <span className="text-white"> world-class services at competitive rates</span> that remain
                lucrative for every organisation we serve.
              </p>

              <div className="mt-8 md:mt-12 flex flex-wrap items-center gap-4">
                <Link to="/contact" className="btn-primary" data-testid="hero-cta-consultation">
                  <Phone className="w-3.5 h-3.5" strokeWidth={1.5} /> Book a Consultation
                </Link>
                <a
                  href="https://wa.me/919643572870"
                  target="_blank" rel="noreferrer"
                  className="btn-ghost-light"
                  data-testid="hero-cta-whatsapp"
                >
                  <MessageCircle className="w-3.5 h-3.5" strokeWidth={1.5} /> WhatsApp Us
                </a>
              </div>
            </div>

            <div className="col-span-12 lg:col-span-3 lg:text-right">
              <p className="kicker text-white/60 mb-3">Defence Colony</p>
              <p className="font-serif text-lg text-white/85">A-197, LGF<br/>New Delhi 110024</p>
              <a href="tel:919643572870" className="block mt-4 font-display text-2xl tracking-wide hover:text-white text-white/85 link-underline">
                +91 96435 72870
              </a>
            </div>
          </div>
        </div>

        <div className="relative z-10 border-t border-white/15">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-2 md:grid-cols-4">
            {stats.map((s, i) => (
              <div
                key={s.label}
                className={`py-6 ${i !== 0 ? "md:border-l border-white/15" : ""} ${i % 2 === 1 ? "border-l border-white/15 md:border-l" : ""}`}
              >
                <p className="font-display text-3xl md:text-4xl">{s.num}</p>
                <p className="kicker text-white/60 mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMMITMENT / ABOUT PREVIEW */}
      <section className="py-16 md:py-32 bg-paper" data-testid="commitment-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-32">
            <p className="kicker mb-6">Our Commitment</p>
            <h2 className="font-display text-4xl md:text-5xl leading-tight text-ink">
              World-class counsel.<br />
              <span className="italic text-brand">Competitive rates.</span>
            </h2>
            <div className="fine-rule w-24 my-8" />
            <p className="font-serif text-xl text-ink/75 leading-relaxed">
              We have built a practice that returns disproportionate value — for every matter, for every organisation. Strategy comes first; litigation, only when it must.
            </p>
            <Link to="/about" className="mt-10 inline-flex items-center gap-2 text-brand font-sans text-xs tracking-[0.25em] uppercase link-underline" data-testid="commitment-about-link">
              About the Firm <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
            </Link>
          </div>

          <div className="lg:col-span-7 relative">
            <div className="aspect-[4/5] overflow-hidden">
              <img src={ABOUT_IMG} alt="Legal documents and pen" className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000" />
            </div>
            <div className="absolute -bottom-8 -left-6 md:-left-12 bg-brand text-white p-8 md:p-12 max-w-md">
              <p className="kicker text-white/70 mb-3">Philosophy</p>
              <p className="font-display text-2xl md:text-3xl leading-tight">
                "Advising clients on how <em className="italic">not to litigate</em>."
              </p>
              <p className="mt-5 font-serif text-white/85 text-base leading-relaxed">
                Pre-litigation guidance, mediation and conciliation — settlements that protect time, money and reputation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES OVERVIEW */}
      <section className="py-16 md:py-32 bg-bone border-y border-rule" id="services" data-testid="services-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-10 mb-16">
            <div className="max-w-2xl">
              <p className="kicker mb-5">Practice Areas</p>
              <h2 className="font-display text-4xl md:text-5xl leading-tight text-ink">
                Thirty practice areas.<br />
                <span className="italic text-brand">One disciplined firm.</span>
              </h2>
            </div>
            <Link to="/services" className="btn-outline self-start md:self-end" data-testid="services-view-all">
              View all services <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 border-t border-l border-rule" data-testid="home-services-grid">
            {services.map((s, i) => (
              <div key={s.slug} className="border-r border-b border-rule">
                <ServiceCard service={s} index={i} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTORS SERVED */}
      <section className="py-16 md:py-32 bg-paper" data-testid="sectors-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
            <div className="lg:col-span-5 lg:sticky lg:top-32">
              <p className="kicker mb-5">Sectors</p>
              <h2 className="font-display text-4xl md:text-5xl leading-tight text-ink">
                Counsel built for <span className="italic text-brand">your industry's</span> realities.
              </h2>
              <div className="fine-rule w-24 my-8" />
              <p className="font-serif text-lg text-ink/75 leading-relaxed">
                Generalist counsel solves yesterday's problem. We bring sector fluency — board-tested, regulator-aware, deal-priced — to every engagement.
              </p>
            </div>
            <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-3 border-t border-l border-rule">
              {[
                "Banking & NBFCs", "Real Estate & Infrastructure", "Manufacturing", "IT & SaaS",
                "Healthcare & Pharma", "Hospitality", "Education", "Media & Entertainment",
                "Retail & E-commerce", "Family Offices", "Start-ups & VC", "Insurance",
              ].map((s) => (
                <div key={s} className="border-r border-b border-rule p-6 md:p-8 hover:bg-bone transition-colors">
                  <p className="font-serif text-lg text-ink/85">{s}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PROCESS / HOW WE WORK */}
      <section className="py-16 md:py-32 bg-bone border-y border-rule" data-testid="process-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mb-16">
            <p className="kicker mb-5">How We Work</p>
            <h2 className="font-display text-4xl md:text-5xl leading-tight text-ink">
              A six-stage <span className="italic text-brand">protocol</span> behind every matter.
            </h2>
            <p className="mt-6 font-serif text-lg text-ink/75 leading-relaxed">
              Every engagement — whether a one-page contract review or multi-year litigation — moves through the same disciplined stages. It is how we keep quality consistent across desks.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-rule border border-rule">
            {[
              { n: "01", t: "Diagnostic Conference", b: "Fact mapping, evidence inventory, exposure analysis and a realistic outcome forecast — before any retainer is signed." },
              { n: "02", t: "Engagement & Scoping", b: "Written scope, milestones, fee architecture and a single point partner contact for the entire matter." },
              { n: "03", t: "Strategic Plan", b: "A written legal strategy memo identifying forums, parallel tracks, risks and the cheapest path to closure." },
              { n: "04", t: "Documentary Build", b: "Pleadings, contracts, evidence vault and witness brief — every page reviewed at partner level before filing." },
              { n: "05", t: "Active Pursuit", b: "Court appearances, negotiation, mediation, and weekly status updates with a digital matter portal for clients." },
              { n: "06", t: "Closure & Enforcement", b: "Decree, settlement, registration, execution — and a closing memo with lessons learned for future risk." },
            ].map((step) => (
              <div key={step.n} className="bg-paper p-10 md:p-12">
                <p className="font-display text-5xl text-brand mb-4">{step.n}</p>
                <h3 className="font-display text-2xl text-ink leading-tight mb-4">{step.t}</h3>
                <p className="font-serif text-base text-ink/70 leading-relaxed">{step.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NOTABLE CLIENTS */}
      <section className="py-16 md:py-32 bg-bone border-y border-rule" data-testid="clients-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="max-w-2xl mb-16">
            <p className="kicker mb-5">Notable Clients</p>
            <h2 className="font-display text-4xl md:text-5xl leading-tight text-ink">
              Trusted by leaders across <span className="italic text-brand">every sector.</span>
            </h2>
            <p className="mt-6 font-serif text-lg text-ink/75 leading-relaxed">
              From global brands and pharma majors to government undertakings and premier academic institutions — organisations rely on K &amp; K Associates for counsel that protects what matters.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 border-t border-l border-rule" data-testid="home-clients-grid">
            {clientSectors.map((cs) => (
              <div key={cs.sector} className="border-r border-b border-rule p-8 md:p-10 bg-paper hover:bg-bone transition-colors">
                <p className="kicker text-brand mb-4">{cs.sector}</p>
                <p className="font-serif text-lg text-ink/80 leading-relaxed">{cs.clients.join(" · ")}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-16 md:py-32 bg-paper" data-testid="testimonials-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="max-w-2xl mb-16">
            <p className="kicker mb-5">Client Voices</p>
            <h2 className="font-display text-4xl md:text-5xl leading-tight text-ink">
              Selected, <span className="italic text-brand">unedited.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-rule border border-rule">
            {[
              {
                q: "They settled a six-year-old dispute in under nine months — without a single court date after the first hearing. The pre-litigation playbook is real.",
                who: "Director, Manufacturing Group",
              },
              {
                q: "We had three law firms before K & K. Theirs is the only file where every page is partner-reviewed before it leaves the office.",
                who: "General Counsel, Listed Co.",
              },
              {
                q: "Cross-border SPA negotiated, closed and CCI cleared — to schedule, to budget. Rare in Indian deal practice.",
                who: "Founder, B2B SaaS",
              },
            ].map((t) => (
              <div key={t.who} className="bg-paper p-10 md:p-12">
                <p className="font-display text-5xl text-brand leading-none mb-6">"</p>
                <p className="font-serif text-xl text-ink leading-snug">{t.q}</p>
                <p className="kicker text-ink/50 mt-8">— {t.who}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOME FAQ — AEO */}
      <section className="py-16 md:py-32 bg-bone border-y border-rule" data-testid="home-faq-section">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12">
          <p className="kicker mb-5">Frequently Asked</p>
          <h2 className="font-display text-4xl md:text-5xl leading-tight text-ink mb-12">
            Questions clients ask <span className="italic text-brand">before</span> they engage us.
          </h2>
          <div className="border-t border-rule">
            {homeFaqs.map((f, i) => (
              <details key={f.q} className="group border-b border-rule py-6 cursor-pointer" data-testid={`home-faq-${i}`}>
                <summary className="flex items-center justify-between gap-6 list-none">
                  <h3 className="font-display text-xl md:text-2xl text-ink leading-snug">{f.q}</h3>
                  <span className="text-brand font-display text-2xl leading-none transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="mt-4 font-serif text-lg text-ink/75 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-16 md:py-32 bg-paper" data-testid="why-choose-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <p className="kicker mb-5">Why K &amp; K</p>
          <h2 className="font-display text-4xl md:text-5xl leading-tight text-ink max-w-3xl">
            Heritage discipline.<br/>
            <span className="italic text-brand">Modern method.</span>
          </h2>
          <div className="fine-rule w-24 mt-8 mb-16" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-rule border border-rule">
            {[
              { Icon: Scale, title: "Trial-Tested Counsel", body: "Court-tested partners with a record of reported judgments across forums." },
              { Icon: Award, title: "Outcome Pricing", body: "Transparent, milestone-linked fees — predictable from kickoff to closure." },
              { Icon: ShieldCheck, title: "Confidentiality First", body: "Strict NDA, secure portals and disciplined evidence-handling protocols." },
              { Icon: Building2, title: "Pan-India Network", body: "Vetted local-counsel network ensures coverage across every Indian court." },
            ].map(({ Icon, title, body }) => (
              <div key={title} className="bg-paper p-10 md:p-12">
                <Icon className="w-7 h-7 text-brand mb-6" strokeWidth={1.25} />
                <h3 className="font-display text-2xl text-ink mb-4">{title}</h3>
                <p className="font-serif text-lg text-ink/70 leading-snug">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-ink text-white py-14 md:py-28 relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-8">
            <p className="kicker text-white/60 mb-5">Begin a Conversation</p>
            <h2 className="font-display text-4xl md:text-5xl leading-tight">
              Tell us your matter.<br />
              <span className="italic text-white/70">We will tell you the path forward.</span>
            </h2>
          </div>
          <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-4">
            <Link to="/contact" className="btn-ghost-light !text-white" data-testid="bottom-cta-consultation">
              Book Consultation <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
            </Link>
            <a
              href="https://wa.me/919643572870"
              target="_blank" rel="noreferrer"
              className="btn-primary border-white"
              data-testid="bottom-cta-whatsapp"
            >
              WhatsApp +91 96435 72870
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
